import { QueryBuilder } from './db';
import { QueryData } from './query-data';
import {
  isRawSQL,
  RawSqlBase,
  templateLiteralToSQL,
} from './expressions/raw-sql';
import { Sql } from './sql/sql';
import { TemplateLiteralArgs } from './expressions/expression';
import { Adapter, QueryResult, QueryResultRow } from '../adapters/adapter';
import { sqlSessionContextGetStateFromAsyncState } from '../adapters/features/sql-session-context';
import { RecordUnknown } from '../utils';
import { TopToSqlCtx, ToSQLCtx } from './sql/to-sql';
import { QueryInternal } from './query-internal';

// Argument for `query` and `queryArrays`, it can be a SQL template literal, or a raw SQL object.
export type SQLQueryArgs = TemplateLiteralArgs | [RawSqlBase];

export interface DbSqlQuery {
  <T extends QueryResultRow = QueryResultRow>(
    ...args: SQLQueryArgs
  ): Promise<QueryResult<T>>;

  /**
   * Returns an array of records:
   *
   * ```ts
   * const array: T[] = await db.$query.records<T>`SELECT * FROM table`;
   * ```
   */
  records<T extends RecordUnknown = RecordUnknown>(
    ...args: SQLQueryArgs
  ): Promise<T[]>;

  /**
   * Returns a single record, throws [NotFoundError](/guide/error-handling) if not found.
   *
   * ```ts
   * const one: T = await db.$query.take<T>`SELECT * FROM table LIMIT 1`;
   * ```
   */
  take<T extends RecordUnknown = RecordUnknown>(
    ...args: SQLQueryArgs
  ): Promise<T>;

  /**
   * Returns a single record or `undefined` when not found.
   *
   * ```ts
   * const maybeOne: T | undefined = await db.$query
   *   .takeOptional<T>`SELECT * FROM table LIMIT 1`;
   * ```
   */
  takeOptional<T extends RecordUnknown = RecordUnknown>(
    ...args: SQLQueryArgs
  ): Promise<T | undefined>;

  /**
   * Returns array of tuples of the values:
   *
   * ```ts
   * const arrayOfTuples: [number, string][] = await db.$query.rows<
   *   [number, string]
   * >`SELECT id, name FROM table`;
   * ```
   */
  rows<T extends unknown[]>(...args: SQLQueryArgs): Promise<T[]>;

  /**
   * Returns a flat array of values for a single column:
   *
   * ```ts
   * const strings: string[] = await db.$query.pluck<string>`SELECT name FROM table`;
   * ```
   */
  pluck<T>(...args: SQLQueryArgs): Promise<T[]>;

  /**
   * Returns a single value, throws [NotFoundError](/guide/error-handling) if not found.
   *
   * ```ts
   * const value: number = await db.$query.get<number>`SELECT 1`;
   * ```
   */
  get<T>(...args: SQLQueryArgs): Promise<T>;

  /**
   * Returns a single value or `undefined` when not found.
   *
   * ```ts
   * const value: number | undefined = await db.$query.getOptional<number>`SELECT 1`;
   * ```
   */
  getOptional<T>(...args: SQLQueryArgs): Promise<T | undefined>;
}

export const performQuery = async <Result = QueryResult>(
  q: {
    qb: QueryBuilder;
    internal: QueryInternal;
    adapterNotInTransaction: Adapter;
    q: QueryData;
  },
  args: SQLQueryArgs,
  method: 'query' | 'arrays',
): Promise<Result> => {
  const trx = q.internal.asyncStorage.getStore();
  let sql: Sql;
  if (isRawSQL(args[0])) {
    const values: unknown[] = [];
    sql = {
      text: args[0].toSQL({ values }),
      values,
    };
  } else {
    const values: unknown[] = [];

    const ctx: ToSQLCtx = {
      topCtx: undefined as never,
      qb: q.qb,
      q: q.q,
      sql: [],
      values,
      selectedCount: 0,
    };
    ctx.topCtx = ctx as TopToSqlCtx;

    sql = {
      text: templateLiteralToSQL(args as TemplateLiteralArgs, ctx),
      values,
    };
  }

  const log = trx?.log ?? q.q.log;
  let logData: unknown | undefined;
  if (log) logData = log.beforeQuery(sql);

  try {
    const adapter = trx?.transactionAdapter || q.adapterNotInTransaction;
    const result = await adapter[method as 'query'](
      sql.text,
      sql.values,
      undefined,
      undefined,
      sqlSessionContextGetStateFromAsyncState(trx),
    );

    if (log) log.afterQuery(sql, logData);

    return result as Result;
  } catch (err) {
    if (log) {
      log.onError(err as Error, sql, logData);
    }

    throw err;
  }
};
